/**
 * Guest-only auth routes (sign-in / register / etc.): pure decision helper.
 *
 * Inverse of RequireCustomerAuth — authenticated users must leave guest screens
 * without a login-form flash. Aligns with G1 OIDC land hold (wait, not form).
 */

export interface GuestOnlyAuthInput {
  readonly sessionHydrated: boolean;
  /** From `isGloballySignedInChrome` (token + not P1-I). */
  readonly globallySignedIn: boolean;
  /**
   * G1 — OIDC land in progress: non-empty getToken() + `?oidcLand=1`.
   * Prefer wait over login form flash while land completes.
   */
  readonly oidcLandingPending?: boolean;
  /**
   * G7 — aggregate land wall-clock budget exceeded. Stop holding auth routes
   * even when land query remains until strip completes.
   */
  readonly oidcLandBudgetExhausted?: boolean;
}

export interface GuestOnlyAuthDecision {
  readonly allowGuestScreen: boolean;
  readonly redirectAway: boolean;
  readonly waitHydration: boolean;
}

export function evaluateGuestOnlyAuth(
  input: GuestOnlyAuthInput
): GuestOnlyAuthDecision {
  if (!input.sessionHydrated) {
    return {
      allowGuestScreen: false,
      redirectAway: false,
      waitHydration: true,
    };
  }

  // G1: token mid-land — hold; never flash the guest form.
  // G7: aggregate budget exhausted — release hold so sign-in can render.
  if (
    input.oidcLandingPending === true &&
    input.oidcLandBudgetExhausted !== true
  ) {
    return {
      allowGuestScreen: false,
      redirectAway: false,
      waitHydration: true,
    };
  }

  if (input.globallySignedIn) {
    return {
      allowGuestScreen: false,
      redirectAway: true,
      waitHydration: false,
    };
  }

  return {
    allowGuestScreen: true,
    redirectAway: false,
    waitHydration: false,
  };
}
